import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { ParticipantRelationship } from '@app/modules/service-data/types/paternity.types';
import {
  Ethnicity,
  Gender,
} from '@app/modules/service-data/types/service-data.types';
import { momentToNgbDate } from '@core/utils/core.util';
import { NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { InputValidationStatusDirective } from '@shared/directives/input-validation-status.directive';
import { controlContainerProvider } from '@shared/helpers';
import moment from 'moment';
import { startWith } from 'rxjs';

export type ParticipantField =
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
  | 'email'
  | 'documentNumber'
  | 'phoneNumber'
  | 'gender'
  | 'relationship'
  | 'ethnicity';

// Renders one test participant inside the parent participant FormGroup
@Component({
  selector: 'app-service-data-form-participant',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    NgbDatepickerModule,
    SelectComponent,
    SelectOptionComponent,
    ValidationErrorComponent,
    InputValidationStatusDirective,
  ],
  templateUrl: './service-data-form-participant.component.html',
  viewProviders: [controlContainerProvider],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDataFormParticipantComponent implements OnInit {
  private controlContainer = inject(ControlContainer);
  private destroyRef = inject(DestroyRef);

  readonly fields = input.required<ParticipantField[]>();
  // Makes element ids unique when several participants are rendered
  readonly idPrefix = input.required<string>();
  // Youngest allowed age in years, 0 only blocks future dates
  readonly minAge = input(0);

  protected readonly Gender = Gender;
  protected readonly ethnicities = Object.values(Ethnicity);
  protected readonly relationships = Object.values(ParticipantRelationship);
  protected readonly Ethnicity = Ethnicity;
  protected readonly momentToNgbDate = momentToNgbDate;
  protected readonly showEthnicityInput = signal(false);
  protected readonly minDate = moment().add(-100, 'year');
  protected readonly birthdayStartDate = moment().subtract(25, 'year');
  protected readonly maxDate = computed(() =>
    momentToNgbDate(moment().subtract(this.minAge(), 'year'))
  );

  protected get form() {
    return this.controlContainer.control as FormGroup;
  }

  ngOnInit(): void {
    const ethnicitySelectControl = this.form.get('ethnicitySelect');

    if (!ethnicitySelectControl) {
      return;
    }

    const ethnicityControl = this.form.get('ethnicity');

    // "Other" ethnicities are typed in, the rest copy the selected value
    ethnicitySelectControl.valueChanges
      .pipe(
        startWith(ethnicitySelectControl.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((ethnicity: Ethnicity) => {
        if (ethnicity === Ethnicity.Other) {
          if (
            Object.values<string>(Ethnicity).includes(ethnicityControl.value)
          ) {
            ethnicityControl.setValue(null);
          }
          this.showEthnicityInput.set(true);
        } else {
          ethnicityControl.setValue(ethnicity);
          this.showEthnicityInput.set(false);
        }
      });
  }
}
